import React, { useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NontAuthorized401 from "NontAuthorized401";
import { apiCall } from "utils/apiClient";

const getDisplayName = (recipient) =>
  `${recipient.name || "Unknown"} (${recipient.mobile || "No contact"})`;
const SMS_LIMIT = 160;
const RECIPIENT_LIMIT = 250;
const MAX_RENDERED_RECIPIENTS = 250;

function PartnerSmsNotification() {
  const [recipientGroup, setRecipientGroup] = useState("customers");
  const [recipientIds, setRecipientIds] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientError, setRecipientError] = useState("");
  const [recipientQuery, setRecipientQuery] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [status, setStatus] = useState("");
  const [replaceTargetId, setReplaceTargetId] = useState(null);
  const replaceInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const createFileSignature = (file) =>
    `${file.name}_${file.size}_${file.lastModified}`;

  const createAttachmentEntry = (file) => ({
    id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    file,
    signature: createFileSignature(file),
    isImage: (file.type || "").startsWith("image/"),
    previewUrl: (file.type || "").startsWith("image/")
      ? URL.createObjectURL(file)
      : "",
  });

  const revokeAttachmentUrl = (attachment) => {
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
  };

  const recipientOptions = useMemo(
    () =>
      recipients.map((recipient, index) => ({
        ...recipient,
        __id: String(recipient.id ?? `legacy-${recipientGroup}-${index}`),
      })),
    [recipients, recipientGroup]
  );

  const filteredRecipients = useMemo(() => {
    const query = recipientQuery.trim().toLowerCase();
    if (!query) {
      return recipientOptions;
    }

    return recipientOptions.filter((recipient) => {
      const name = (recipient.name || "Unknown").toLowerCase();
      const contact = (recipient.mobile || recipient.email || "").toLowerCase();
      const id = String(recipient.id || "").toLowerCase();
      return (
        name.includes(query) ||
        contact.includes(query) ||
        id.includes(query) ||
        recipientGroup.includes(query)
      );
    });
  }, [recipientGroup, recipientOptions, recipientQuery]);

  const allRecipientIds = useMemo(
    () => recipientOptions.map((recipient) => recipient.__id),
    [recipientOptions]
  );

  const isAllSelected =
    recipientOptions.length > 0 && recipientIds.length === recipientOptions.length;
  const isMessageLong = message.length > SMS_LIMIT;

  useEffect(() => {
    const fetchRecipients = async () => {
      setLoadingRecipients(true);
      setRecipientError("");

      try {
        const body = {
          page: 1,
          limit: RECIPIENT_LIMIT,
        };
        const result = await apiCall({
          endpoint:
            recipientGroup === "customers" ? "customer/getCustomer" : "partner/list",
          method: "POST",
          data: body,
        });

        if (result.status === 200) {
          setRecipients(Array.isArray(result.data) ? result.data : []);
        } else {
          setRecipients([]);
          setRecipientError("Unable to load recipients.");
        }
      } catch (error) {
        setRecipients([]);
        setRecipientError("Failed to load recipients.");
      } finally {
        setLoadingRecipients(false);
      }
    };

    setRecipientIds([]);
    setRecipientQuery("");
    setAttachments((currentAttachments) => {
      currentAttachments.forEach((attachment) => revokeAttachmentUrl(attachment));
      return [];
    });
    fetchRecipients();
    setStatus("");
  }, [recipientGroup]);

  const handleRecipientGroupChange = (value) => {
    setRecipientGroup(value);
    setReplaceTargetId(null);
    setStatus("");
  };

  useEffect(() => {
    if (!messageInputRef.current) {
      return;
    }

    messageInputRef.current.style.height = "auto";
    const nextHeight = Math.min(messageInputRef.current.scrollHeight, 420);
    messageInputRef.current.style.height = `${nextHeight}px`;
  }, [message]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setRecipientIds(allRecipientIds);
    } else {
      setRecipientIds([]);
    }
  };

  const toggleRecipient = (event, id) => {
    if (event.target.checked) {
      setRecipientIds((current) =>
        current.includes(id) ? current : [...current, id]
      );
      return;
    }
    setRecipientIds((current) => current.filter((item) => item !== id));
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    setAttachments((currentAttachments) => {
      const existingKeys = new Set(
        currentAttachments.map((attachment) => attachment.signature)
      );
      const mergedAttachments = [...currentAttachments];

      files.forEach((file) => {
        const fileKey = createFileSignature(file);
        if (!existingKeys.has(fileKey)) {
          existingKeys.add(fileKey);
          mergedAttachments.push(createAttachmentEntry(file));
        }
      });

      return mergedAttachments;
    });
    event.target.value = "";
  };

  const removeAttachment = (targetId) => {
    setAttachments((currentAttachments) => {
      const found = currentAttachments.find(
        (attachment) => attachment.id === targetId
      );
      if (found) {
        revokeAttachmentUrl(found);
      }
      return currentAttachments.filter((attachment) => attachment.id !== targetId);
    });
  };

  const openReplacePicker = (targetId) => {
    setReplaceTargetId(targetId);
    if (replaceInputRef.current) {
      replaceInputRef.current.value = "";
      replaceInputRef.current.click();
    }
  };

  const handleAttachmentReplace = (event) => {
    const file = Array.from(event.target.files || [])[0];

    if (!file || !replaceTargetId) {
      return;
    }

    setAttachments((currentAttachments) =>
      currentAttachments.map((attachment) => {
        if (attachment.id !== replaceTargetId) {
          return attachment;
        }

        revokeAttachmentUrl(attachment);
        return {
          ...attachment,
          file,
          signature: createFileSignature(file),
          isImage: (file.type || "").startsWith("image/"),
          previewUrl: (file.type || "").startsWith("image/")
            ? URL.createObjectURL(file)
            : "",
        };
      })
    );

    setReplaceTargetId(null);
    event.target.value = "";
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (recipientIds.length === 0) {
      setStatus("Please select at least one recipient.");
      return;
    }

    const targetText =
      recipientIds.length === recipientOptions.length
        ? `all ${recipientGroup}`
        : `${recipientIds.length} selected ${recipientGroup}`;
    const attachmentText = attachments.length
      ? ` with ${attachments.length} attachment(s)`
      : "";

    setStatus(
      `Ready to send SMS when API is connected: ${message.length}-char message${attachmentText} to ${targetText}.`
    );
  };

  return (
    <>
      {localStorage.getItem("Token") ? (
        <DashboardLayout>
          <DashboardNavbar />
          <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-cyan-50 px-2 py-3 sm:px-3 sm:py-4">
            <div className="w-full max-w-full">
              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_18px_50px_-28px_rgba(15,23,42,0.45)] md:p-5">
                <div className="mb-4 flex flex-col gap-2 border-b border-slate-100 pb-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                      SMS Notification
                    </h2>
                    <p className="mt-0.5 text-sm text-slate-600">
                      Send a short text message to customers or partners.
                    </p>
                  </div>
                  <p className="text-xs font-semibold tracking-wide text-slate-500 md:text-sm">
                    Select Recipient
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="grid gap-4">
                  <div className="grid gap-2">
                    <label className="text-sm font-medium text-slate-700">
                      Recipient Group
                    </label>
                    <div className="inline-flex w-full rounded-xl border border-slate-200 bg-slate-50 p-1 md:w-auto">
                      <button
                        type="button"
                        onClick={() => handleRecipientGroupChange("customers")}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                          recipientGroup === "customers"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-500 hover:bg-white/70"
                        }`}
                      >
                        Customers
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRecipientGroupChange("partners")}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                          recipientGroup === "partners"
                            ? "bg-white text-slate-900 shadow-sm"
                            : "text-slate-500 hover:bg-white/70"
                        }`}
                      >
                        Partners
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-2 lg:grid-cols-[1fr_1.45fr]">
                    <div className="grid gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <label
                          htmlFor="sms-attachments"
                          className="inline-flex cursor-pointer items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                        >
                          Upload images / files
                        </label>
                        <span className="text-xs text-slate-500">
                          {attachments.length} file(s) selected
                        </span>
                      </div>
                        <input
                          id="sms-attachments"
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                          multiple
                          onChange={handleAttachmentChange}
                          className="hidden"
                        />
                        <input
                          ref={replaceInputRef}
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                          onChange={handleAttachmentReplace}
                          className="hidden"
                        />
                        {attachments.length > 0 && (
                          <ul className="mt-2 flex flex-wrap gap-2">
                            {attachments.map((attachment) => (
                              <li
                                key={attachment.id}
                                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700 sm:w-[17rem]"
                              >
                                <div className="flex items-start gap-2">
                                  {attachment.isImage ? (
                                    <a
                                      href={attachment.previewUrl}
                                      target="_blank"
                                      rel="noreferrer noopener"
                                      className="inline-flex"
                                    >
                                      <img
                                        src={attachment.previewUrl}
                                        alt={attachment.file.name}
                                        className="h-12 w-12 rounded-md border border-slate-200 object-cover"
                                      />
                                    </a>
                                  ) : (
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-[11px] text-slate-500">
                                      File
                                    </div>
                                  )}
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate">{attachment.file.name}</p>
                                    <p className="text-[11px] text-slate-500">
                                      {(attachment.file.size / 1024).toFixed(1)} KB
                                    </p>
                                    <div className="flex gap-2">
                                      {attachment.isImage && (
                                        <a
                                          href={attachment.previewUrl}
                                          target="_blank"
                                          rel="noreferrer noopener"
                                          className="rounded-md bg-slate-100 px-2 py-1 text-slate-700 transition hover:bg-slate-200"
                                        >
                                          View
                                        </a>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => openReplacePicker(attachment.id)}
                                        className="rounded-md bg-slate-100 px-2 py-1 text-slate-700 transition hover:bg-slate-200"
                                      >
                                        Replace
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => removeAttachment(attachment.id)}
                                        className="rounded-md bg-red-50 px-2 py-1 text-red-600 transition hover:bg-red-100"
                                        title="Remove file"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Message
                      </label>
                      <textarea
                        value={message}
                        onChange={(event) => setMessage(event.target.value)}
                        ref={messageInputRef}
                        rows={1}
                        className="w-full min-h-[3rem] resize-none overflow-hidden rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        placeholder="Type SMS body..."
                      />
                      <p
                        className={`mt-0.5 text-xs ${
                          isMessageLong ? "text-red-600" : "text-slate-500"
                        }`}
                      >
                        {message.length}/{SMS_LIMIT} characters
                        {isMessageLong ? " (Warning: may exceed SMS limit)" : ""}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <div className="mb-1 text-sm font-medium text-slate-700">
                      Recipient List
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={recipientQuery}
                        onChange={(event) => setRecipientQuery(event.target.value)}
                        className="h-11 flex-1 rounded-xl border border-slate-300 bg-white px-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                        placeholder="Search by name, email, or mobile"
                      />
                      <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          disabled={loadingRecipients || recipientOptions.length === 0}
                          className="size-4 accent-blue-600"
                        />
                        Select all {recipientGroup}
                      </label>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white">
                      <div className="grid gap-3 border-b border-slate-100 p-3 text-sm text-slate-500 md:grid-cols-2">
                        <div>{recipientOptions.length} total {recipientGroup}</div>
                        <div className="text-right">
                          {recipientQuery ? (
                            <>
                              {filteredRecipients.length} shown
                              {` of ${recipientOptions.length}`}
                            </>
                          ) : (
                            `${recipientIds.length} selected`
                          )}
                        </div>
                      </div>

                      {loadingRecipients ? (
                        <p className="px-4 py-8 text-center text-sm text-slate-500">
                          Loading {recipientGroup}...
                        </p>
                      ) : recipientError ? (
                        <p className="px-4 py-8 text-center text-sm text-red-500">
                          {recipientError}
                        </p>
                      ) : filteredRecipients.length === 0 ? (
                        <p className="px-4 py-8 text-center text-sm text-slate-500">
                          No recipients found for this search.
                        </p>
                      ) : (
                        <div className="max-h-[22rem] overflow-y-auto">
                          <ul className="divide-y divide-slate-100">
                            {filteredRecipients
                              .slice(0, MAX_RENDERED_RECIPIENTS)
                              .map((recipient) => {
                              const checked = recipientIds.includes(recipient.__id);

                              return (
                                <li
                                  key={recipient.__id}
                                  className={`grid cursor-pointer grid-cols-[auto,1fr] items-center gap-3 px-4 py-3 transition ${
                                    checked ? "bg-blue-50/80" : "hover:bg-slate-50"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(event) =>
                                      toggleRecipient(event, recipient.__id)
                                    }
                                    className="size-4 accent-blue-600"
                                  />
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-slate-900">
                                      {getDisplayName(recipient)}
                                    </p>
                                    <p className="truncate text-xs text-slate-500">
                                      ID: {recipient.__id}
                                    </p>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="inline-flex w-fit items-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                    disabled={recipientIds.length === 0}
                  >
                    Prepare & Send Draft
                  </button>
                </form>

                {status && (
                  <p className="mt-2 rounded-xl bg-blue-50 px-3 py-2 text-sm text-blue-800">
                    {status}
                  </p>
                )}
              </section>
            </div>
          </div>
        </DashboardLayout>
      ) : (
        <NontAuthorized401 />
      )}
    </>
  );
}

export default PartnerSmsNotification;
